import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import type { TimeframeSelection } from '../../types/timeframe';
import AdvancedDatepickerPanel from './AdvancedDatepickerPanel';
import styles from './datepickerTrigger.module.css';

const GRANULARITY_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

interface DatepickerTriggerProps {
  /** Current committed timeframe. */
  value: TimeframeSelection;
  /** Called when user clicks Apply inside the panel. */
  onChange: (selection: TimeframeSelection) => void;
  /** Label above the trigger (default: "Time frame"). */
  label?: string;
  /** Earliest selectable month. */
  minDate?: moment.Moment;
  /** Latest selectable month. */
  maxDate?: moment.Moment;
}

function formatTriggerText(startDate: string, endDate: string): string {
  const start = moment(startDate).format('MMM D, YYYY');
  const end = moment(endDate).format('MMM D, YYYY');
  if (startDate === endDate) return start;
  return `${start} - ${end}`;
}

const DatepickerTrigger: React.FC<DatepickerTriggerProps> = ({
  value,
  onChange,
  label = 'Time frame',
  minDate,
  maxDate,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleApplyFromPanel = (selection: TimeframeSelection) => {
    setOpen(false);
    onChange(selection);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={styles.triggerWrapper} ref={wrapperRef}>
      {label && (
        <div className={styles.triggerLabel}>
          {label}
          <span className={styles.helpIconWrapper}>
            <span
              className={styles.helpIcon}
              aria-describedby="timeframe-help-tooltip"
              tabIndex={0}
            >
              ?
            </span>
            <span
              id="timeframe-help-tooltip"
              role="tooltip"
              className={styles.tooltip}
            >
              Select the time period and granularity for your report data.
            </span>
          </span>
        </div>
      )}
      <div className={styles.triggerRow}>
        <div
          className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
          onClick={() => setOpen(!open)}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && setOpen(!open)}
        >
          <span className={styles.aggregateBadge}>
            {GRANULARITY_LABELS[value.granularity] || value.granularity}
          </span>
          <svg className={styles.calendarIcon} viewBox="0 0 16 16" width="18" height="18" aria-hidden="true">
            <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <line x1="1.5" y1="6" x2="14.5" y2="6" stroke="currentColor" strokeWidth="1.3" />
            <line x1="4.5" y1="1" x2="4.5" y2="3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            <line x1="11.5" y1="1" x2="11.5" y2="3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          <span className={styles.dateText}>
            {formatTriggerText(value.startDate, value.endDate)}
          </span>
          <svg
            className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
            viewBox="0 0 16 9"
            width="16"
            height="9"
            aria-hidden="true"
          >
            <polyline
              points="1,1 8,8 15,1"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      {open && (
        <AdvancedDatepickerPanel
          initialValue={value}
          onApply={handleApplyFromPanel}
          onCancel={handleCancel}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}
    </div>
  );
};

export default DatepickerTrigger;
